import { pick } from "lodash-es"
import { CalendarDuration } from "@oysterhr/schema"
import { RestrictiveCovenantsRulesFragment } from "~/gql/graphql"
import {
  DurationOption,
  EmploymentRestrictiveCovenants,
  NonSolicitationOptionsEnum,
  PostTerminationSection,
  RestrictiveCovenantsFormValues,
} from "../types"
import { parseAmountType, parsePaymentFrequency, parseRestrictedAreas } from "./parseRules"
import { getMappedServerToClientValues } from "./serverClientMapping"
import { getPaymentSectionVisibility } from "../hooks/usePaymentSectionVisibility"
import { getStatutoryPaymentValue } from "./getStatutoryPaymentValue"

type InitializationParams = {
  employment: EmploymentRestrictiveCovenants
  initialValuesFunction?: (
    employment: EmploymentRestrictiveCovenants,
  ) => RestrictiveCovenantsFormValues
  knowledgeBase?: RestrictiveCovenantsRulesFragment
  currentFormValues?: RestrictiveCovenantsFormValues
}

export const initializeValuesWithKnowledgeBase = (params: InitializationParams) => {
  const { employment, initialValuesFunction } = params
  const defaultValuesWithoutKnowledgeBase = initialValuesFunction?.(employment)

  return {
    // Non-disclosure/Non-competition/Non-Solicitation
    ...defaultValuesWithoutKnowledgeBase,
    ...initializeNonSolicitationOption(params),
    postTerminationSection: {
      // Duration Section
      ...initializeDuration(params),
      // Payment Section
      ...initializeAmountType(params),
      // @todo: in a future PR I'm thinking that instead of making the amountType reactive
      // in order to update the paymentFrequency options, I can predict the amountType future
      // value using the `initializeAmountType(params)`, and initialize the paymentFrequency
      // to what it should be in the future once the amountType was initialized.
      ...initializePaymentFrequency(params),
      ...initializePaymentAmount(params),
      // Acknowledgement Section
      ...initializeAcknowledgement(params),
      // Restricted Area Section
      ...initializeRestrictedArea(params),
      // Company's Area of Business Section
      ...initializeAreaOfBusiness(params),
      // Additional areas section
      ...initializeAdditionalAreas(params),
    },
  } as RestrictiveCovenantsFormValues
}

const initializeNonSolicitationOption = (params: InitializationParams) => {
  const { employment, initialValuesFunction, currentFormValues, knowledgeBase } = params
  const defaultValuesWithoutKnowledgeBase = initialValuesFunction?.(employment)
  const currentFormValue = currentFormValues?.nonSolicitationOption

  /** `true` if knowledge base doesn't exist */
  const allowBothAsDefaultValue = knowledgeBase?.restrictedClauses
    ? knowledgeBase?.restrictedClauses?.nonSolicitationCustomers &&
      knowledgeBase.restrictedClauses.nonSolicitationEmployees
    : true
  const isCurrentValueBoth = currentFormValue === NonSolicitationOptionsEnum.Both

  const defaultValueWithKnowledgeBase = allowBothAsDefaultValue
    ? currentFormValue ?? defaultValuesWithoutKnowledgeBase?.nonSolicitationOption
    : isCurrentValueBoth && !allowBothAsDefaultValue
      ? null
      : currentFormValue
  return {
    nonSolicitationOption: defaultValueWithKnowledgeBase,
  }
}

const initializePaymentAmount = (params: InitializationParams) => {
  const serverMappedValues = getMappedServerToClientValues(params.employment, params.knowledgeBase)
  const { knowledgeBase, currentFormValues } = params

  const kbStatutoryPaymentValue = getStatutoryPaymentValue({
    currentValues: currentFormValues,
    knowledgeBase,
  })
  const { statutoryPaymentVisible } = getPaymentSectionVisibility({
    currentValues: currentFormValues,
    knowledgeBase,
  })

  const { amountPercentage: statutoryKbPercentage, amountFixed: statutoryKbFixed } =
    kbStatutoryPaymentValue || {}

  /*
   * Ignore current form values if payment is statutory.
   * This means that statutory payment is always overwritten with the appropriate KB rule.
   */
  const currentFixed = statutoryPaymentVisible
    ? null
    : currentFormValues?.postTerminationSection?.amountFixed
  const currentPercentage = statutoryPaymentVisible
    ? null
    : currentFormValues?.postTerminationSection?.amountPercentage

  return {
    amountFixed:
      currentFixed || (serverMappedValues?.postTerminationSection?.amountFixed ?? statutoryKbFixed),
    amountPercentage:
      currentPercentage ||
      (serverMappedValues?.postTerminationSection?.amountPercentage ?? statutoryKbPercentage),
  }
}

const initializeAmountType = (params: InitializationParams) => {
  const serverMappedValues = getMappedServerToClientValues(params.employment, params.knowledgeBase)
  const defaultValuesWithoutKnowledgeBase = params?.initialValuesFunction?.(params.employment)

  const amountType =
    parseAmountType({
      currentValues: params?.currentFormValues,
      knowledgeBase: params?.knowledgeBase,
    }).requiredAmountType ??
    // @todo: not sure if this second one should be here, but it seems to work.
    // Testing should clear it up.
    // --------------------------------------------------
    params?.currentFormValues?.postTerminationSection?.amountType ??
    // --------------------------------------------------
    serverMappedValues?.postTerminationSection?.amountType ??
    defaultValuesWithoutKnowledgeBase?.postTerminationSection?.amountType

  return { amountType }
}

const initializePaymentFrequency = (params: InitializationParams) => {
  const serverMappedValues = getMappedServerToClientValues(params.employment, params.knowledgeBase)
  const defaultValuesWithoutKnowledgeBase = params?.initialValuesFunction?.(params.employment)

  const paymentFrequency =
    parsePaymentFrequency({
      currentValues: params?.currentFormValues,
      knowledgeBase: params?.knowledgeBase,
    }).requiredPaymentFrequency ??
    params?.currentFormValues?.postTerminationSection?.paymentFrequency ??
    serverMappedValues?.postTerminationSection?.paymentFrequency ??
    defaultValuesWithoutKnowledgeBase?.postTerminationSection?.paymentFrequency

  return { paymentFrequency }
}

const initializeDuration = (params: InitializationParams) => {
  const serverMappedValues = getMappedServerToClientValues(params.employment, params.knowledgeBase)

  const currentFormPostTerminationSection = params?.currentFormValues?.postTerminationSection
  const serverPostTerminationSection = serverMappedValues?.postTerminationSection
  const kbSuggestedOptions = params?.knowledgeBase?.postTermination?.suggestedDurations

  const toDurationOption = (duration: CalendarDuration) =>
    `${duration.value}_${duration.unit}` as DurationOption

  const durationValueExists = (postTerminationSection?: PostTerminationSection) => {
    const { durationOption, durationCustom } = postTerminationSection || {}
    if (durationCustom != null) {
      return true
    }
    if (durationOption != null) {
      return (
        !kbSuggestedOptions?.length ||
        kbSuggestedOptions?.map(toDurationOption).includes(durationOption)
      )
    }
    return (
      durationCustom != null ||
      (durationOption && kbSuggestedOptions?.map(toDurationOption).includes(durationOption))
    )
  }

  if (durationValueExists(currentFormPostTerminationSection)) {
    return pick(currentFormPostTerminationSection, "durationOption", "durationCustom")
  }
  if (durationValueExists(serverPostTerminationSection)) {
    return pick(serverPostTerminationSection, "durationOption", "durationCustom")
  }
}

const initializeAcknowledgement = (params: InitializationParams) => {
  const serverMappedValues = getMappedServerToClientValues(params.employment, params.knowledgeBase)

  const acknowledged =
    params?.currentFormValues?.postTerminationSection?.acknowledged ??
    serverMappedValues?.postTerminationSection?.acknowledged

  return { acknowledged }
}

const initializeRestrictedArea = (params: InitializationParams) => {
  const serverMappedValues = getMappedServerToClientValues(params.employment, params.knowledgeBase)

  const { isApplicable } = parseRestrictedAreas({
    currentValues: params?.currentFormValues,
    knowledgeBase: params?.knowledgeBase,
  })

  const restrictedArea =
    params?.currentFormValues?.postTerminationSection?.restrictedArea ??
    serverMappedValues?.postTerminationSection?.restrictedArea

  return { restrictedArea: isApplicable ? restrictedArea : null }
}

const initializeAreaOfBusiness = (params: InitializationParams) => {
  const serverMappedValues = getMappedServerToClientValues(params.employment, params.knowledgeBase)
  return {
    areaOfBusiness:
      params.currentFormValues?.postTerminationSection?.areaOfBusiness ??
      serverMappedValues?.postTerminationSection?.areaOfBusiness,
    areaOfBusinessInLocalLanguage:
      params.currentFormValues?.postTerminationSection?.areaOfBusinessInLocalLanguage ??
      serverMappedValues?.postTerminationSection?.areaOfBusinessInLocalLanguage,
  }
}

const initializeAdditionalAreas = (params: InitializationParams) => {
  const serverMappedValues = getMappedServerToClientValues(params.employment, params.knowledgeBase)
  const { additionalAreasAllowed } = parseRestrictedAreas({
    currentValues: params?.currentFormValues,
    knowledgeBase: params?.knowledgeBase,
  })

  return {
    additionalAreas: additionalAreasAllowed
      ? params.currentFormValues?.postTerminationSection?.additionalAreas ??
        serverMappedValues.postTerminationSection?.additionalAreas
      : null,
  }
}
